/** Bazaar orders expire 7 days after they are placed (wiki: Bazaar). Counted from when the order was added here. */
export const ORDER_LIFETIME_MS = 7 * 86400_000;
export const expiresAt = (o) => o.createdAt + ORDER_LIFETIME_MS;
/** You claimed `units` (default: everything filled so far) in game. */
export const claimOrder = (o, units = o.filled) => ({ ...o, claimed: Math.min(o.filled, Math.max(o.claimed ?? 0, units)) });
const cents = (p) => Math.round(p * 100);
const levels = (b, side) => (side === "buy" ? b.bids : b.asks);
/** strictly better for the order book side: a higher buy order, a lower sell offer */
const better = (side, a, b) => (side === "buy" ? cents(a) > cents(b) : cents(a) < cents(b));
function look(side, price, b) {
    const ls = levels(b, side);
    let level = 0, ahead = 0;
    for (const l of ls) {
        if (cents(l.price) === cents(price))
            level += l.amount;
        else if (better(side, l.price, price))
            ahead += l.amount;
    }
    return { best: ls[0]?.price ?? null, level, better: ahead };
}
/** Start following an order you just placed (your units are assumed to be the last ones at your price). */
export function trackOrder(o, b) {
    const l = look(o.side, o.price, b);
    const best = l.best;
    const status = best == null || !better(o.side, best, o.price) ? "top" : "behind";
    const seen = l.level >= o.amount;
    return { ...o, createdAt: b.ts, ahead: seen ? l.level - o.amount : 0, filled: 0, confirmed: false, seen, status, best, level: l.level, better: l.better,
        buyWeek: b.buyWeek, sellWeek: b.sellWeek, updatedAt: b.ts };
}
/** One new snapshot: queue position, fills and status. Returns the events worth telling you about. */
export function updateOrder(o, b) {
    if (o.status === "filled" || o.expired || b.ts <= o.updatedAt)
        return { order: o, events: [] };
    if (b.ts >= expiresAt(o)) {
        const order = { ...o, expired: true, updatedAt: b.ts };
        return { order, events: [{ type: "expired", order }] };
    }
    const l = look(o.side, o.price, b);
    const events = [];
    let { ahead, filled } = o;
    // real trades since the last update: instant sells fill buy orders, instant buys fill sell offers, best price first
    const rise = o.side === "buy" ? (o.sellWeek != null ? b.sellWeek - o.sellWeek : -1) : (o.buyWeek != null ? b.buyWeek - o.buyWeek : -1);
    const fallBuy = o.buyWeek != null && b.buyWeek < o.buyWeek, fallSell = o.sellWeek != null && b.sellWeek < o.sellWeek;
    const trades = rise >= 0 && !fallBuy && !fallSell ? rise : 0; // a counter fell (expiry batch): trades unknown this time
    const reachedUs = Math.max(0, trades - o.better); // better-priced units fill first
    const shrink = Math.max(0, o.level - l.level);
    const consumed = Math.max(shrink, reachedUs);
    const seen = o.seen || l.level >= o.amount - o.filled;
    if (!o.seen && seen)
        ahead = Math.max(0, l.level - (o.amount - o.filled)); // first time we see it: everything before it is ahead
    else if (o.seen) {
        ahead -= consumed;
        if (ahead < 0) {
            filled += -ahead;
            ahead = 0;
        }
        // your remaining units are still in the level: it cannot hold fewer units than you still have
        filled = Math.max(filled, o.amount - l.level);
    }
    filled = Math.min(o.amount, Math.max(0, filled));
    let status, confirmed = false;
    if (seen && (l.best == null || (better(o.side, o.price, l.best) && l.level === 0))) {
        // the best price moved strictly past yours and your price level is gone: used up
        status = "filled";
        filled = o.amount;
        confirmed = true;
    }
    else if (filled >= o.amount)
        status = "filled";
    else
        status = l.best != null && better(o.side, l.best, o.price) ? "behind" : "top";
    const next = { ...o, ahead, filled, confirmed, seen, status, best: l.best, level: l.level, better: l.better, buyWeek: b.buyWeek, sellWeek: b.sellWeek, updatedAt: b.ts };
    if (status === "filled")
        events.push({ type: "filled", order: next, confirmed });
    else {
        if (filled > o.filled)
            events.push({ type: "partial", order: next, filled: filled - o.filled });
        if (status === "behind" && o.status !== "behind" && l.best != null)
            events.push({ type: "outbid", order: next, by: Math.abs(l.best - o.price), relist: Math.round((o.side === "buy" ? l.best + 0.1 : l.best - 0.1) * 10) / 10 });
        if (status === "top" && o.status === "behind")
            events.push({ type: "top", order: next });
    }
    return { order: next, events };
}
/** Sell first: buy orders whose units filled but have no sell offer tracked yet (`<id>-sell`). Buying more before these
 *  are listed ties up coins and lets purchases outpace sales. */
export function unsoldBuys(orders) {
    const sells = new Set(orders.filter(o => o.side === "sell").map(o => o.id));
    return orders.filter(o => o.side === "buy" && o.filled > 0 && !sells.has(`${o.id}-sell`));
}
//# sourceMappingURL=order-tracker.js.map